import type {
  FigureFormat,
  FundFigureKey,
  FundMetricsPoint,
} from '@/types/fund';
import {
  formatCurrencyCompact,
  formatCurrencyNoDecimals,
  formatNumber,
  formatPercentage,
} from '@/utils/formatters';

export function figureValue(
  point: FundMetricsPoint,
  figure: FundFigureKey
): number | null {
  return point[figure] ?? null;
}

export function formatFigure(
  value: number | null,
  format: FigureFormat,
  currency: string
): string {
  if (value === null) return 'n/m';
  switch (format) {
    case 'amount':
      return formatCurrencyNoDecimals(value, currency);
    case 'multiple':
      return `${value.toFixed(2)}x`;
    case 'percent':
      return formatPercentage(value, 1);
    case 'count':
      return formatNumber(value, { maximumFractionDigits: 0 });
  }
}

export function formatFigureAxis(
  value: number,
  format: FigureFormat,
  currency: string
): string {
  switch (format) {
    case 'amount':
      return value === 0
        ? formatCurrencyNoDecimals(0, currency)
        : formatCurrencyCompact(value, currency);
    case 'percent':
      return formatPercentage(value, 0);
    default:
      return formatFigure(value, format, currency);
  }
}

function changeSign(change: number) {
  if (change > 0) return '+';
  if (change < 0) return '−';
  return '±';
}

export function formatFigureChange(
  change: number,
  format: FigureFormat,
  currency: string
): string {
  const sign = changeSign(change);
  const magnitude = Math.abs(change);
  if (format === 'percent') return `${sign}${(magnitude * 100).toFixed(1)} pp`;
  return `${sign}${formatFigure(magnitude, format, currency)}`;
}

export function quarterLabel(asOf: string): string {
  const [year, month] = asOf.split('-').map(Number);
  return `Q${Math.ceil(month / 3)} ${year}`;
}

export function shortQuarterLabel(asOf: string): string {
  const [year, month] = asOf.split('-').map(Number);
  return `Q${Math.ceil(month / 3)} ’${String(year).slice(2)}`;
}
